import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./eliminate-zone-modal.css";

import { injectIntl, intlShape } from "react-intl";
import { messages } from "../../i18n-messages";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import {
    DialogBox,
    DialogBoxFooterType,
    Loader,
    NumericInput,
    RadioButton,
    RadioButtonGroup,
} from "~/core";
import { logFirebaseEvent } from "~/utils/firebase";
import { EliminationType, IZoneEliminateOptions } from "@ai360/core";
import {
    useLoginUserPreferences,
    useUpdateLoginUserPreferences,
} from "~/core/components/login-user-preferences";
import { getZoneEliminateOptions } from "~/map/components/map-tools/selectors";
import { setZoneEliminateOptions } from "~/map/components/map-tools/actions";

const ZONE_ELIMINATE = "ZoneEliminate";
const ZONE_ELIMINATION_OPTIONS = "zoneEliminationOptions";

interface IEliminateZoneModalProps {
    intl: intlShape;
    onClose: (
        eliminateZones: boolean,
        eliminationType: EliminationType,
        minimumArea: number
    ) => void;
    onUpdateZones: () => void;
    zoneEliminateOptions: IZoneEliminateOptions;
}

export const EliminateZoneModal_ = (props: IEliminateZoneModalProps) => {
    const dispatch = useAppDispatch();
    const zoneEliminateOptions = useAppSelector(getZoneEliminateOptions);

    const [eliminationType, setEliminationType] = useState<EliminationType>(
        zoneEliminateOptions.eliminationType
    );

    const [minimumArea, setMinimumArea] = useState<number>(zoneEliminateOptions.minimumArea);

    const { loginUserPreferences, isLoading } = useLoginUserPreferences(ZONE_ELIMINATE, [
        ZONE_ELIMINATION_OPTIONS,
    ]);
    const { savePreference } = useUpdateLoginUserPreferences(ZONE_ELIMINATE);

    const handleSave = async () => {
        const eliminateZoneOptions = {
            eliminationType: eliminationType,
            minimumArea: minimumArea,
        };

        await savePreference(ZONE_ELIMINATION_OPTIONS, JSON.stringify(eliminateZoneOptions));
    };

    useEffect(() => {
        if (loginUserPreferences?.zoneEliminationOptions) {
            try {
                const eliminateZoneOptions = JSON.parse(
                    loginUserPreferences.zoneEliminationOptions
                ) as IZoneEliminateOptions;

                if (eliminateZoneOptions) {
                    if (eliminateZoneOptions.eliminationType !== null) {
                        zoneEliminateOptions.eliminationType = eliminateZoneOptions.eliminationType;
                        setEliminationType(eliminateZoneOptions.eliminationType);
                    }

                    if (eliminateZoneOptions.minimumArea !== null) {
                        zoneEliminateOptions.minimumArea = eliminateZoneOptions.minimumArea;
                        setMinimumArea(eliminateZoneOptions.minimumArea);
                    }
                }
            } catch (error) {
                console.error("Error parsing areaDimensions:", error);
            }
        }
    }, [isLoading, setEliminationType, setMinimumArea]);

    const getIsValueOutOfRange = useCallback(
        (minimumArea: number) => {
            if (minimumArea == null) {
                minimumArea = zoneEliminateOptions.minimumArea;
            }
            return isNaN(minimumArea) || minimumArea < 0.001;
        },
        [minimumArea]
    );

    const getInstructionsPart = useCallback(() => {
        const { formatMessage } = props.intl;
        return (
            <div className="instructions">
                {eliminationType === EliminationType.LARGEST ? (
                    <div>{formatMessage(messages.zoneEliminateModalInstructionsLargest)}</div>
                ) : null}
                {eliminationType === EliminationType.LONGEST ? (
                    <div>{formatMessage(messages.zoneEliminateModalInstructionsLongest)}</div>
                ) : null}
            </div>
        );
    }, [props.intl, eliminationType]);

    const getMinimumAreaPart = useCallback(() => {
        const { formatMessage } = props.intl;

        const areaOutOfRange = getIsValueOutOfRange(minimumArea);
        const outOfRangeMsg = !areaOutOfRange ? null : (
            <div className="out-of-range-msg">
                {formatMessage(messages.zoneEliminateAreaOutOfRange)}
            </div>
        );

        return (
            <div className="minimum-area">
                <div className="dim-input">
                    <NumericInput
                        containerClassNames={[{ "form-input-error": areaOutOfRange }]}
                        onChange={(v, f, numVal) => {
                            if (numVal != minimumArea) {
                                setMinimumArea(numVal);
                            }
                        }}
                        placeholderText={formatMessage(messages.zoneEliminateMinimumAreaLblTxt)}
                        precision={9}
                        scale={4}
                        value={minimumArea}
                    />
                </div>
                {outOfRangeMsg}
            </div>
        );
    }, [minimumArea, getIsValueOutOfRange]);

    const getRadioBtnGroupPart = useCallback(() => {
        const { formatMessage } = props.intl;

        return (
            <div className="eliminate-source">
                <div className="radio-grp-lbl">
                    {formatMessage(messages.zoneEliminateMethodLblTxt)}
                </div>
                <RadioButtonGroup
                    className="radio-grp-source"
                    selectedValue={eliminationType}
                    afterOnChange={(val: string) => setEliminationType(val as EliminationType)}
                >
                    <RadioButton
                        value={EliminationType.LONGEST}
                        label={formatMessage(messages.zoneEliminateLongestLblTxt)}
                    />
                    <RadioButton
                        value={EliminationType.LARGEST}
                        label={formatMessage(messages.zoneEliminateLargestLblTxt)}
                    />
                </RadioButtonGroup>
            </div>
        );
    }, [eliminationType, props.intl]);

    const isValid = useMemo(() => {
        return typeof minimumArea === "number" && !isNaN(minimumArea) && minimumArea >= 0.001;
    }, [minimumArea]);

    const handleClose = useCallback(
        async (eliminateZones: boolean) => {
            if (eliminateZones) {
                await handleSave();
                dispatch(
                    setZoneEliminateOptions({
                        eliminationType,
                        minimumArea,
                    })
                );
            }
            props.onClose(eliminateZones, eliminationType, minimumArea);
        },
        [eliminationType, minimumArea, props.onClose, handleSave]
    );

    return (
        <DialogBox
            draggable
            forceOverflow
            isOpen
            unrestricted
            action={props.intl.formatMessage(messages.zoneEliminateEliminateZonesBtnTxt)}
            className="zone-eliminate-modal"
            footerType={DialogBoxFooterType.ACTION_CANCEL}
            actionDisabled={!isValid}
            onAction={() => {
                logFirebaseEvent(`zones_eliminate_${eliminationType}`);
                handleClose(true);
            }}
            onClose={() => {
                handleClose(false);
            }}
            title={props.intl.formatMessage(messages.zoneEliminateModalTitle)}
        >
            {isLoading ? <Loader className="eliminate-zone-loader" /> : null}
            {getRadioBtnGroupPart()}
            {getMinimumAreaPart()}
            {getInstructionsPart()}
        </DialogBox>
    );
};

export const EliminateZoneModal = injectIntl(EliminateZoneModal_);
